// Per-venue freshness + retained-data UI for My Trades (#125/#158), moved
// verbatim out of index.tsx (#341). Pairs with the existing
// freshnessUi.test.tsx, which had been named for this module since #125 while
// the components themselves still lived in the view.
//
// Both classify and self-tick from RAW evidence (status + last-success time)
// rather than from a parent-computed state, which is what lets the page avoid
// owning a clock. Neither fetches: the retry is a callback supplied by the page.

import { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  shouldShowFreshnessBadge,
  formatFreshnessAge,
  type VenueRawStatus,
} from './venueFreshness';
import { useVenueFreshness } from './useVenueFreshness';

export function VenueFreshnessBadge({
  venue,
  status,
  lastSuccessMs,
  staleThresholdMs,
  onRetry,
}: {
  venue: string;
  status: VenueRawStatus;
  lastSuccessMs: number | null;
  staleThresholdMs: number | null;
  onRetry: () => void | Promise<void>;
}) {
  const { state, ageMs, refreshing } = useVenueFreshness(status, lastSuccessMs, staleThresholdMs);
  // Track the retry in-flight locally: the venue status enums only flip to
  // 'loading' from 'idle', so `refreshing` alone never covers a retry from an
  // error/retained state. Awaiting onRetry here gives real per-venue feedback.
  const [retrying, setRetrying] = useState(false);
  if (!shouldShowFreshnessBadge(state)) return null;
  const busy = refreshing || retrying;
  const detail =
    state === 'unavailable'
      ? 'not connected'
      : state === 'error'
        ? "couldn't load"
        : ageMs != null
          ? `synced ${formatFreshnessAge(ageMs)} ago`
          : 'retained';
  return (
    <button
      type="button"
      onClick={async () => {
        setRetrying(true);
        try {
          await onRetry();
        } finally {
          setRetrying(false);
        }
      }}
      disabled={busy}
      title={`${venue} — click to refresh now`}
      aria-label={`${venue} ${detail}${busy ? ' — refreshing' : ''} — refresh`}
      className="border-amber-500/50 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors disabled:opacity-60"
    >
      <RefreshCw size={11} className={busy ? 'animate-spin' : ''} />
      {venue} · {busy ? 'refreshing…' : detail}
    </button>
  );
}

export interface VenueEvidence {
  venue: string;
  status: VenueRawStatus;
  lastSuccessMs: number | null;
  staleThresholdMs: number | null;
}

/** Banner naming any venue whose rows are retained (stale/failed refresh, or
 *  Poly not-connected) so the retained data is labeled, never silently shown as
 *  current (#125). Renders nothing when both venues are fresh. Self-ticks from
 *  raw evidence (like the badge) so the parent page doesn't (#158). */
export function RetainedDataBanner({ kalshi, poly }: { kalshi: VenueEvidence; poly: VenueEvidence }) {
  const kf = useVenueFreshness(kalshi.status, kalshi.lastSuccessMs, kalshi.staleThresholdMs);
  const pf = useVenueFreshness(poly.status, poly.lastSuccessMs, poly.staleThresholdMs);
  // `ageMs != null` means the venue actually loaded once, so there are rows to
  // retain. Without it a first-load 'unavailable' (never succeeded, zero rows)
  // would falsely claim "showing retained rows" (#125).
  const retained = [
    { venue: kalshi.venue, f: kf },
    { venue: poly.venue, f: pf },
  ].filter(
    (r) => (r.f.state === 'retained' || r.f.state === 'unavailable') && r.f.ageMs != null,
  );
  if (retained.length === 0) return null;
  return (
    <div className="space-y-0.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300/90">
      {retained.map((r) => {
        const age = r.f.ageMs != null ? formatFreshnessAge(r.f.ageMs) : null;
        const reason = r.f.state === 'unavailable' ? 'not connected' : 'refresh delayed or failed';
        return (
          <div key={r.venue}>
            Showing retained {r.venue} rows{age ? ` — last synced ${age} ago` : ''} ({reason}).
          </div>
        );
      })}
    </div>
  );
}
